// Logix AI ERP — in-app AI Assistant panel.
// Right-side chat drawer wired to the backend proxy (api/assistant/chat),
// which holds the Anthropic API key and the full-ERP knowledge prompt.
// The assistant answers in whatever language the user writes.
import { Component, ElementRef, ViewChild, signal, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { LogixOrbComponent } from './logix-orb.component';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// A parsed piece of an assistant reply: plain text, bold text, or an in-app link.
interface MsgToken {
  t: 'text' | 'bold' | 'link';
  v: string;        // display text
  path?: string;    // internal route (for links only)
}

// Phase-6 Part B: a gated action the agent proposes — the panel renders a
// Confirm/Cancel card and, on Confirm, runs it via api/assistant/execute.
interface AssistantAction {
  tool: string;
  tier: number;        // 1 Write · 2 Financial
  title: string;
  summary: string;
  paramsJson: string;
}

@Component({
  selector: 'app-assistant-panel',
  standalone: true,
  imports: [CommonModule, LogixOrbComponent],
  template: `
    @if (open()) {
      <div class="fixed inset-0 z-50 flex justify-end">
        <div class="absolute inset-0 bg-slate-900/40" (click)="close()"></div>

        <div class="relative w-full max-w-md bg-surface h-full shadow-pop flex flex-col border-l border-slate-200">

          <!-- Header -->
          <div class="px-5 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
            <div class="flex items-center gap-3">
              <app-logix-orb [size]="36" [active]="thinking() || listening()" />
              <div class="leading-tight">
                <div class="font-display text-[14px] font-bold text-slate-800">Logix AI Assistant</div>
                <div class="text-[11px] text-secondary">Ask anything about the ERP — in any language</div>
              </div>
            </div>
            <div class="flex items-center gap-1">
              <!-- Voice language (for mic + read-aloud) -->
              <button (click)="toggleVoiceLang()"
                class="px-2 py-1 rounded-md text-[10.5px] font-bold tracking-wide border border-slate-200 text-secondary hover:text-brand hover:border-brand/40 transition-colors"
                title="Voice language (speech input and read-aloud)">
                {{ voiceLang() === 'ur-PK' ? 'اردو' : 'EN' }}
              </button>
              <!-- Read replies aloud -->
              <button (click)="toggleSpeakReplies()"
                [class]="speakReplies()
                  ? 'p-1.5 rounded-md bg-brand-50 text-brand'
                  : 'p-1.5 rounded-md hover:bg-canvas text-secondary'"
                title="Read replies aloud">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4.5 h-4.5" style="width:17px;height:17px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
                </svg>
              </button>
              <button (click)="close()" class="p-1.5 rounded-md hover:bg-canvas text-secondary" title="Close">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Messages -->
          <div #scrollHost class="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">

            @if (messages().length === 0) {
              <div class="text-center mt-8 px-4">
                <div class="text-[14px] font-semibold text-slate-700 mb-1.5">How can I help you today?</div>
                <div class="text-[12.5px] text-secondary leading-relaxed mb-4">
                  I know every module of Logix AI ERP. Ask me how to do something,
                  what a screen means, or why something happened.
                </div>
                <div class="flex flex-col gap-2">
                  @for (s of suggestions; track s) {
                    <button (click)="send(s)"
                      class="text-left text-[12.5px] px-3.5 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:border-brand/40 hover:bg-brand-50 hover:text-brand transition-colors">
                      {{ s }}
                    </button>
                  }
                </div>
              </div>
            }

            @for (m of messages(); track $index) {
              @if (m.role === 'user') {
                <div class="self-end max-w-[85%] bg-brand text-white text-[13px] leading-relaxed rounded-2xl rounded-br-md px-3.5 py-2.5 whitespace-pre-wrap">{{ m.content }}</div>
              } @else {
                <div class="self-start max-w-[90%] bg-canvas border border-slate-200 text-slate-700 text-[13px] leading-relaxed rounded-2xl rounded-bl-md px-3.5 py-2.5 whitespace-pre-wrap">@for (tok of messageTokens(m.content); track $index) {@if (tok.t === 'link') {<button (click)="navigate(tok.path!)" title="Open this screen in the ERP" class="inline-flex items-center gap-1 my-0.5 px-2.5 py-1 rounded-lg bg-gradient-to-r from-brand to-brand-700 text-white text-[12px] font-semibold shadow-sm hover:brightness-110 transition align-middle whitespace-normal"><svg xmlns="http://www.w3.org/2000/svg" style="width:13px;height:13px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>{{ tok.v }}</button>} @else if (tok.t === 'bold') {<strong class="font-semibold text-slate-800">{{ tok.v }}</strong>} @else {<span>{{ tok.v }}</span>}}</div>
              }
            }

            @if (pendingAction(); as act) {
              <div class="self-start w-full max-w-[92%] bg-surface border border-brand/30 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div class="flex items-center gap-2 mb-1.5">
                  <span class="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-brand-50 text-brand shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" style="width:14px;height:14px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  <div class="text-[12.5px] font-semibold text-slate-800">{{ act.title }}</div>
                  @if (act.tier === 2) {
                    <span class="ml-auto text-[9.5px] font-bold uppercase tracking-wide text-danger bg-danger/10 px-1.5 py-0.5 rounded">Financial</span>
                  }
                </div>
                <div class="text-[12.5px] text-slate-600 leading-relaxed mb-3">{{ act.summary }}</div>
                <div class="flex items-center gap-2">
                  <button (click)="confirmAction()" [disabled]="confirming()"
                    class="px-3.5 py-1.5 rounded-lg bg-brand text-white text-[12px] font-semibold hover:bg-brand-600 disabled:opacity-50 transition-colors">
                    {{ confirming() ? 'Working…' : 'Confirm' }}
                  </button>
                  <button (click)="cancelAction()" [disabled]="confirming()"
                    class="px-3.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-[12px] font-semibold hover:bg-canvas disabled:opacity-50 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            }

            @if (thinking()) {
              <div class="self-start bg-canvas border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-secondary animate-bounce" style="animation-delay:0ms"></span>
                <span class="w-1.5 h-1.5 rounded-full bg-secondary animate-bounce" style="animation-delay:150ms"></span>
                <span class="w-1.5 h-1.5 rounded-full bg-secondary animate-bounce" style="animation-delay:300ms"></span>
              </div>
            }

            @if (error()) {
              <div class="self-stretch text-[12.5px] text-warning-700 bg-warning-50 border border-warning/20 rounded-lg px-3.5 py-2.5 leading-relaxed">{{ error() }}</div>
            }
          </div>

          <!-- Composer -->
          <div class="px-4 py-3.5 border-t border-slate-200 shrink-0">
            <div class="flex items-end gap-2">
              <textarea #box rows="1" [placeholder]="listening() ? 'Listening… speak your command, words will appear live' : 'Type or speak your question in any language...'"
                (keydown.enter)="onEnter($event, box)"
                class="flex-1 resize-none px-3.5 py-2.5 text-[13px] rounded-xl bg-canvas text-slate-700 placeholder:text-slate-400 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent max-h-32"></textarea>
              <!-- Mic — press, speak, words appear live in bar, silence stops mic -->
              <button (click)="toggleListening(box)"
                [disabled]="transcribing()"
                [class]="listening()
                  ? 'w-10 h-10 rounded-xl bg-danger text-white flex items-center justify-center animate-pulse transition-colors shrink-0 shadow-md ring-2 ring-danger/40'
                  : (transcribing() ? 'w-10 h-10 rounded-xl bg-brand-50 border border-brand text-brand flex items-center justify-center animate-pulse shrink-0'
                  : 'w-10 h-10 rounded-xl bg-canvas border border-slate-200 text-secondary hover:text-brand hover:border-brand/40 flex items-center justify-center transition-colors shrink-0')"
                [title]="listening() ? 'Listening… speak your command, pause will stop mic, or tap to stop' : (transcribing() ? 'Transcribing with Gemini...' : 'Speak your question (Gemini voice)')">
                @if (transcribing()) {
                  <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" style="width:17px;height:17px;" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                } @else {
                  <svg xmlns="http://www.w3.org/2000/svg" style="width:17px;height:17px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                  </svg>
                }
              </button>
              <button (click)="sendFromBox(box)" [disabled]="thinking() || transcribing()"
                class="w-10 h-10 rounded-xl bg-brand text-white flex items-center justify-center hover:bg-brand-600 disabled:opacity-50 transition-colors shrink-0" title="Send">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4.5 h-4.5" style="width:17px;height:17px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
              </button>
            </div>
            @if (transcribing()) {
              <div class="text-[11px] font-semibold text-brand mt-1.5 text-center flex items-center justify-center gap-1.5 animate-pulse">
                <span class="w-2 h-2 rounded-full bg-brand"></span>
                <span>Gemini AI aawaz ko text me convert kar raha hai...</span>
              </div>
            } @else if (listening()) {
              <div class="text-[11px] font-semibold text-danger mt-1.5 text-center flex items-center justify-center gap-1.5 animate-pulse">
                <span class="w-2 h-2 rounded-full bg-danger"></span>
                @if (speechDetected()) {
                  <span>Aawaz suni ja rahi hai... Bolte rahein (chup hone par mic band ho jayega)</span>
                } @else {
                  <span>Listening ({{ voiceLang() === 'ur-PK' ? 'اردو' : 'English' }})... Bolna shuru karein</span>
                }
              </div>
            } @else {
              <div class="text-[10.5px] text-secondary mt-1.5 text-center flex items-center justify-center gap-2">
                <span>Powered by Gemini · tap mic to speak</span>
                <button (click)="toggleVoiceLang()" class="text-brand font-semibold underline text-[10.5px]">
                  {{ voiceLang() === 'ur-PK' ? 'Zaban: اردو (Switch to EN)' : 'Lang: English (اردو karein)' }}
                </button>
              </div>
            }
          </div>
        </div>
      </div>
    }
  `
})
export class AssistantPanelComponent {
  open = signal(false);
  messages = signal<ChatMessage[]>([]);
  thinking = signal(false);
  error = signal<string | null>(null);

  // Phase-6 Part B — a proposed Write/Financial action awaiting the user's Confirm.
  pendingAction = signal<AssistantAction | null>(null);
  confirming = signal(false);

  // ---- Voice (Dual Engine: Real-Time Live Dictation + Server Gemini Audio Transcriber) ----
  listening = signal(false);              // mic actively capturing speech
  speechDetected = signal(false);         // speech audio actively arriving
  transcribing = signal(false);           // Gemini AI transcribing audio in background
  audioLevel = signal(0);                 // real-time audio volume
  speakReplies = signal(false);           // read assistant replies aloud
  voiceLang = signal<'ur-PK' | 'en-US'>('en-US'); // Default to en-US for high accuracy on ERP commands & mixed speech, switchable to ur-PK
  private recognition: any = null;        // webkitSpeechRecognition instance
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private audioCtx: any = null;
  private analyser: any = null;
  private animFrameId: any = null;
  private baseText = '';                  // text already in box before mic started
  private silenceTimer: any = null;       // silence timer to auto-stop mic when user stops talking
  private manualStop = false;             // true when user or silence ended the session
  private readonly silenceMs = 2200;      // 2.2s silence stops mic (leaving text in box ready to send)
  private readonly urduRe = new RegExp('[\\u0600-\\u06FF]'); // Urdu/Arabic script detector (TTS language)
  private ttsAudio: HTMLAudioElement | null = null;         // currently-playing spoken reply
  private ttsSeq = 0;                                        // token: bumping it cancels an in-flight speak sequence
  private readonly sentenceEndRe = new RegExp('[.!?\\u06D4](\\s|$)'); // sentence end incl. Urdu '۔'

  suggestions = [
    'Which products are low on stock?',
    'Who owes us the most money?',
    'What are this month\'s sales?',
    'How do I add a new customer?'
  ];

  @ViewChild('scrollHost') scrollHost?: ElementRef<HTMLDivElement>;

  constructor(private http: HttpClient, private router: Router, private ngZone: NgZone) {}

  /**
   * Split an assistant reply into text / bold / in-app-link tokens.
   * Markdown links whose target starts with '/' (e.g. [Open Warehouse](/pss/core/warehouse))
   * become clickable chips that navigate the ERP in place. **bold** is rendered bold.
   */
  messageTokens(content: string): MsgToken[] {
    const tokens: MsgToken[] = [];
    const re = /\[([^\]]+)\]\((\/[^)\s]+)\)|\*\*([^*]+)\*\*/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      if (m.index > last) tokens.push({ t: 'text', v: content.slice(last, m.index) });
      if (m[2] !== undefined) tokens.push({ t: 'link', v: m[1], path: m[2] });
      else tokens.push({ t: 'bold', v: m[3] });
      last = re.lastIndex;
    }
    if (last < content.length) tokens.push({ t: 'text', v: content.slice(last) });
    return tokens;
  }

  /** Navigate the ERP to a module IN PLACE — no new tab, and the assistant stays open. */
  navigate(path: string): void {
    this.router.navigateByUrl(path);
  }

  show(): void { this.open.set(true); }

  close(): void {
    this.open.set(false);
    this.cleanupVoice();
    this.stopAudio();
  }

  toggleVoiceLang(): void {
    this.voiceLang.set(this.voiceLang() === 'ur-PK' ? 'en-US' : 'ur-PK');
    if (this.listening() && this.recognition) {
      try {
        this.recognition.lang = this.voiceLang();
      } catch { /* ignore */ }
    }
  }

  toggleSpeakReplies(): void {
    this.speakReplies.update(v => !v);
    if (!this.speakReplies()) this.stopAudio();   // turning it off stops any playback
  }

  /**
   * Press mic and speak: words appear LIVE in the writing bar in real time (just like gemini.google.com).
   * When you pause/finish speaking, the mic automatically stops, leaving your text in the writing bar
   * so you can review and send it. If browser SpeechRecognition is throttled on the cloud domain,
   * Gemini Audio Transcriber seamlessly processes the voice input as fallback!
   */
  toggleListening(box: HTMLTextAreaElement): void {
    if (this.listening() || this.transcribing()) {
      this.stopListening(box);
      return;
    }

    this.error.set(null);
    this.baseText = box.value ? box.value.trim() + ' ' : '';
    this.audioChunks = [];
    this.manualStop = false;

    // 1. Start browser SpeechRecognition synchronously in user gesture
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SR) {
      try {
        this.recognition = new SR();
        this.recognition.lang = this.voiceLang();
        this.recognition.interimResults = true;
        this.recognition.continuous = true;
        this.recognition.maxAlternatives = 1;

        this.recognition.onstart = () => {
          this.ngZone.run(() => {
            this.listening.set(true);
            this.speechDetected.set(false);
          });
        };

        this.recognition.onspeechstart = () => {
          this.ngZone.run(() => {
            this.speechDetected.set(true);
          });
        };

        this.recognition.onresult = (e: any) => {
          this.ngZone.run(() => {
            this.speechDetected.set(true);
            let interim = '';
            let finalized = '';
            for (let i = 0; i < e.results.length; i++) {
              const res = e.results[i];
              if (res.isFinal) {
                finalized += res[0].transcript + ' ';
              } else {
                interim += res[0].transcript;
              }
            }
            const full = (this.baseText + finalized + interim).trim();
            box.value = full;
            box.dispatchEvent(new Event('input'));
            this.resetSilenceTimer(box);
          });
        };

        this.recognition.onerror = (e: any) => {
          this.ngZone.run(() => {
            if (e?.error === 'no-speech' || e?.error === 'aborted') return;
            if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
              this.error.set('Microphone access was blocked. Please allow the microphone for this site in your browser.');
            }
          });
        };

        this.recognition.onend = () => {
          this.ngZone.run(() => {
            if (this.listening() && !this.manualStop) {
              this.stopListening(box);
            }
          });
        };

        this.recognition.start();
        this.listening.set(true);
      } catch (srErr) {
        console.warn('SpeechRecognition unavailable, relying on MediaRecorder audio stream:', srErr);
      }
    }

    // 2. In parallel, record high-quality audio via MediaRecorder & monitor volume via AudioContext
    if (navigator?.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      }).then((stream) => {
        this.mediaStream = stream;
        this.ngZone.run(() => {
          this.listening.set(true);
        });

        // Setup real-time audio volume & silence detector
        try {
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          if (AudioContextClass) {
            this.audioCtx = new AudioContextClass();
            const source = this.audioCtx.createMediaStreamSource(stream);
            this.analyser = this.audioCtx.createAnalyser();
            this.analyser.fftSize = 256;
            source.connect(this.analyser);

            const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
            let lastSoundTime = Date.now();

            const checkAudio = () => {
              if (!this.listening() || !this.analyser) return;
              this.analyser.getByteFrequencyData(dataArray);
              let sum = 0;
              for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
              const avg = sum / dataArray.length;
              const level = Math.min(100, Math.round((avg / 128) * 100));
              this.audioLevel.set(level);

              if (level > 7) {
                this.ngZone.run(() => this.speechDetected.set(true));
                lastSoundTime = Date.now();
                this.resetSilenceTimer(box);
              } else if (this.speechDetected() && Date.now() - lastSoundTime > 2200) {
                // 2.2s of silence detected -> auto-stop mic!
                this.ngZone.run(() => this.stopListening(box));
                return;
              }

              this.animFrameId = requestAnimationFrame(checkAudio);
            };
            this.animFrameId = requestAnimationFrame(checkAudio);
          }
        } catch (actxErr) {
          console.warn('AudioContext monitor warning:', actxErr);
        }

        // Setup MediaRecorder
        try {
          const mime = (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm')) ? 'audio/webm'
                     : (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/mp4')) ? 'audio/mp4'
                     : '';
          const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
          this.mediaRecorder = mr;
          mr.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) this.audioChunks.push(e.data);
          };
          mr.onstop = () => {
            const blob = new Blob(this.audioChunks, { type: mr.mimeType || 'audio/webm' });
            // If Web Speech API was unable to transcribe any text, send audio to Gemini transcribe endpoint!
            const currentVal = (box.value ?? '').trim();
            const hadBaseOnly = currentVal === this.baseText.trim();
            if ((!currentVal || hadBaseOnly) && blob.size > 1500) {
              this.transcribeAudioWithGemini(blob, box);
            }
          };
          mr.start(200);
        } catch (mrErr) {
          console.warn('MediaRecorder warning:', mrErr);
        }
      }).catch((permErr) => {
        this.ngZone.run(() => {
          if (permErr?.name === 'NotAllowedError' || permErr?.name === 'PermissionDeniedError') {
            this.error.set('Microphone access was blocked. Please allow microphone access in your browser address bar.');
          }
        });
      });
    }
  }

  private transcribeAudioWithGemini(blob: Blob, box: HTMLTextAreaElement): void {
    this.ngZone.run(() => this.transcribing.set(true));
    const reader = new FileReader();
    reader.onloadend = () => {
      const b64 = (reader.result as string)?.split(',')[1];
      if (!b64) {
        this.ngZone.run(() => this.transcribing.set(false));
        return;
      }
      this.http.post<{ text?: string; error?: string }>('/api/assistant/transcribe', {
        audioBase64: b64,
        mimeType: blob.type || 'audio/webm'
      }).subscribe({
        next: (res) => {
          this.ngZone.run(() => {
            this.transcribing.set(false);
            if (res?.text) {
              box.value = (this.baseText + res.text).trim();
              box.dispatchEvent(new Event('input'));
              try { box.focus(); } catch { /* ignore */ }
            } else if (res?.error && res.error !== 'No audio received.') {
              this.error.set(res.error);
            }
          });
        },
        error: () => {
          this.ngZone.run(() => this.transcribing.set(false));
        }
      });
    };
    reader.readAsDataURL(blob);
  }

  /** Restart the silence countdown on every new word. */
  private resetSilenceTimer(box: HTMLTextAreaElement): void {
    clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.stopListening(box);
      });
    }, this.silenceMs);
  }

  private cleanupVoice(): void {
    clearTimeout(this.silenceTimer);
    this.manualStop = true;
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    if (this.audioCtx) {
      try { this.audioCtx.close(); } catch { /* ignore */ }
      this.audioCtx = null;
      this.analyser = null;
    }
    try { this.recognition?.stop(); } catch { /* ignore */ }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try { this.mediaRecorder.stop(); } catch { /* ignore */ }
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(t => t.stop());
      this.mediaStream = null;
    }
    this.listening.set(false);
    this.speechDetected.set(false);
    this.audioLevel.set(0);
  }

  /** End dictation — mic automatically turns off, leaving recognized text in the box so user can send it. */
  stopListening(box: HTMLTextAreaElement): void {
    this.cleanupVoice();
    try { box.focus(); } catch { /* ignore */ }
  }

  // Strip Markdown so the voice speaks EXACTLY the words shown, never the
  // symbols. Removes link syntax (keeps the visible label), bold/italic
  // markers, inline code, headings, blockquotes and bullet markers — the
  // "asterisk asterisk" problem the user reported.
  private toSpeech(md: string): string {
    return (md ?? '')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')   // [label](/path) -> label
      .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, '$1') // **bold** / *italic* / _x_
      .replace(/`([^`]+)`/g, '$1')                // `code` -> code
      .replace(/^#{1,6}\s+/gm, '')                // # headings
      .replace(/^\s*>+\s?/gm, '')                 // > blockquotes
      .replace(/^\s*[-*•]\s+/gm, '')              // bullet markers
      .replace(/[*_`#>]/g, '')                    // any leftover symbols
      .replace(/[ \t]{2,}/g, ' ')
      .trim();
  }

  /**
   * Reads an assistant reply aloud when Speak replies is on. Speaks the CLEAN
   * text (no Markdown symbols) using Gemini's human-quality, multilingual TTS
   * via the backend. To START FAST it speaks the FIRST SENTENCE as its own tiny
   * clip (returns in ~1s) while the REST is synthesised in the background and
   * played straight after — so the voice begins almost immediately instead of
   * waiting for the whole reply to render. Falls back to the browser engine.
   */
  private speak(text: string): void {
    if (!this.speakReplies()) return;
    const clean = this.toSpeech(text);
    if (!clean) return;

    this.stopAudio();                       // also invalidates any running sequence
    const myToken = ++this.ttsSeq;
    // First sentence first (small = fast), remainder as one more clip (few calls = kind to quota).
    this.speakChunks(this.splitForSpeech(clean), myToken);
  }

  /** Fetch one TTS clip as a promise (base64 WAV, or null). */
  private fetchClip(text: string): Promise<string | null> {
    return new Promise((resolve) => {
      this.http.post<{ audio?: string; error?: string }>('/api/assistant/tts', { text })
        .subscribe({
          next: (res) => resolve(res.audio ?? null),
          error: () => resolve(null)
        });
    });
  }

  /** Play base64 WAV clips in order, prefetching the next while the current plays. */
  private async speakChunks(chunks: string[], token: number): Promise<void> {
    if (chunks.length === 0) return;
    let next = this.fetchClip(chunks[0]);           // start the first synth immediately
    for (let i = 0; i < chunks.length; i++) {
      if (token !== this.ttsSeq || !this.speakReplies()) return;
      const clip = await next;
      next = (i + 1 < chunks.length) ? this.fetchClip(chunks[i + 1]) : Promise.resolve(null);
      if (token !== this.ttsSeq || !this.speakReplies()) return;
      if (!clip) { this.speakFallback(chunks.slice(i).join(' ')); return; }  // TTS unavailable → browser voice for the rest
      await this.playClip(clip, token);
    }
  }

  /** Play one base64 WAV clip; resolves when it finishes (or fails). */
  private playClip(b64: string, token: number): Promise<void> {
    return new Promise((resolve) => {
      if (token !== this.ttsSeq) { resolve(); return; }
      const a = new Audio('data:audio/wav;base64,' + b64);
      this.ttsAudio = a;
      a.onended = () => resolve();
      a.onerror = () => resolve();
      a.play().catch(() => resolve());
    });
  }

  /** Split into speech chunks: first sentence alone (fast start), the rest as one clip. */
  private splitForSpeech(text: string): string[] {
    const idx = text.search(this.sentenceEndRe);    // first sentence end (incl. Urdu '۔')
    if (idx > 0 && idx < text.length - 2) {
      const first = text.slice(0, idx + 1).trim();
      const rest = text.slice(idx + 1).trim();
      return rest ? [first, rest] : [first];
    }
    return [text];
  }

  /** Browser speech-engine backup used only when Gemini TTS is unavailable. */
  private speakFallback(clean: string): void {
    if (!this.speakReplies() || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(clean);
    // Urdu/Arabic script (U+0600-U+06FF) -> ur-PK voice; otherwise English.
    u.lang = this.urduRe.test(clean) ? 'ur-PK' : 'en-US';
    u.rate = 1;
    window.speechSynthesis.speak(u);
  }

  /** Stop any in-flight spoken audio (Gemini clip + fallback) and cancel the sequence. */
  private stopAudio(): void {
    this.ttsSeq++;                          // invalidate any running speakChunks pipeline
    if (this.ttsAudio) { try { this.ttsAudio.pause(); } catch { /* ignore */ } this.ttsAudio = null; }
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }

  sendFromBox(box: HTMLTextAreaElement): void {
    if (this.listening()) {
      this.stopListening(box);
    }
    const val = (box.value ?? '').trim();
    if (!val || this.thinking()) return;
    box.value = '';
    box.dispatchEvent(new Event('input'));
    this.send(val);
  }

  onEnter(event: Event, box: HTMLTextAreaElement): void {
    const e = event as KeyboardEvent;
    if (e.shiftKey) return;           // Shift+Enter = new line
    e.preventDefault();
    this.sendFromBox(box);
  }

  send(text: string): void {
    const content = (text ?? '').trim();
    if (!content || this.thinking()) return;

    this.error.set(null);
    this.pendingAction.set(null);            // a new question supersedes any stale proposal
    this.messages.update(list => [...list, { role: 'user', content }]);
    this.thinking.set(true);
    this.scrollSoon();

    this.http.post<{ reply?: string; error?: string; action?: AssistantAction }>('/api/assistant/chat', {
      messages: this.messages()
    }).subscribe({
      next: (res) => {
        this.thinking.set(false);
        if (res.error) {
          this.error.set(res.error);
        } else {
          if (res.reply) {
            this.messages.update(list => [...list, { role: 'assistant', content: res.reply! }]);
            this.speak(res.reply);
          }
          if (res.action) this.pendingAction.set(res.action);   // show the Confirm/Cancel card
        }
        this.scrollSoon();
      },
      error: () => {
        this.thinking.set(false);
        this.error.set('Could not reach the assistant. Please check that the backend is running and try again.');
      }
    });
  }

  /** The user pressed Confirm on a proposed action — run it via the backend. */
  confirmAction(): void {
    const act = this.pendingAction();
    if (!act || this.confirming()) return;
    this.confirming.set(true);
    this.error.set(null);

    this.http.post<{ success?: boolean; reply?: string; error?: string }>('/api/assistant/execute', {
      tool: act.tool, paramsJson: act.paramsJson
    }).subscribe({
      next: (res) => {
        this.confirming.set(false);
        this.pendingAction.set(null);
        if (res.error) {
          this.error.set(res.error);
        } else if (res.reply) {
          this.messages.update(list => [...list, { role: 'assistant', content: res.reply! }]);
          this.speak(res.reply);
        }
        this.scrollSoon();
      },
      error: () => {
        this.confirming.set(false);
        this.error.set('Could not complete the action. Please try again.');
      }
    });
  }

  /** The user pressed Cancel — discard the proposed action. */
  cancelAction(): void {
    this.pendingAction.set(null);
    this.messages.update(list => [...list, { role: 'assistant', content: 'Okay, I have cancelled that action.' }]);
    this.scrollSoon();
  }

  private scrollSoon(): void {
    setTimeout(() => {
      const el = this.scrollHost?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }
}
